/**
 * The Favorites deck, arrangeable (the window saves the order on the server).
 *
 * Arranging is an explicit mode (the window's Arrange / Done): cards then move instead of
 * running or flipping. Drag one with a mouse, or with a finger after a short still hold
 * (a swipe still scrolls the page) — it floats under the pointer,
 * its place shows as an empty slot, and the others slide aside as it passes over them —
 * or move the focused card with the arrow keys (each move is announced). Escape or Enter
 * ends arranging.
 *
 * The slots are the grid's places and don't move while a card is dragged (only what is in
 * them does), so the drop target is read from where the slots were when the drag began —
 * the target never jitters under cards that are still sliding. They are kept relative to
 * the grid, so the page scrolling under a held card (a wheel, or the edge auto-scroll
 * when the card is held near the top or bottom) still drops it where it is.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { moveItem, slotAt, columnsOf, type Box } from './cardNav'

const reducedMotion = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
const boxOf = (r: DOMRect): Box => ({ left: r.left, top: r.top, width: r.width, height: r.height })
/** The nearest ancestor that scrolls vertically (the window's own scroller), if any. */
function scrollerOf(el: HTMLElement | null): HTMLElement | null {
  for (let n = el?.parentElement ?? null; n; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n
  }
  return null
}
/** How close to the scroller's edge (px) a held card starts scrolling it, and how fast. */
const EDGE = 56
const EDGE_SPEED = 14
/** A finger picks a card up after holding still this long (ms); moving first is a scroll. */
const TOUCH_HOLD_MS = 260
/** How far (px) a finger may drift during the hold. */
const TOUCH_SLOP = 8

export function DeckGrid({ ids, arranging, narrow, renderCard, renderFloat, extra, onReorder, onMoved, onExit }: {
  /** The deck's card ids, in its order. */
  ids: readonly string[]
  arranging: boolean
  narrow: boolean
  renderCard: (id: string, opts: { placeholder: boolean; index: number }) => ReactNode
  /** The card that floats under the pointer while it is dragged. */
  renderFloat: (id: string) => ReactNode
  /** After the cards (the empty slots) — not while arranging. */
  extra?: ReactNode
  onReorder: (ids: string[]) => void
  /** A card landed at `position` (1-based) of `total` — for the announcement. */
  onMoved: (id: string, position: number, total: number) => void
  onExit: () => void
}) {
  const gridRef = useRef<HTMLDivElement>(null)
  const idsRef = useRef(ids)
  idsRef.current = ids
  const [drag, setDrag] = useState<{ id: string; x: number; y: number; dx: number; dy: number; w: number } | null>(null)
  const press = useRef<{ id: string; pointerId: number; x: number; y: number; dx: number; dy: number; w: number; moving: boolean; touch: boolean; held: boolean; timer: number } | null>(null)
  /** The slots, relative to the grid's top-left corner when the drag began. */
  const slots = useRef<Box[]>([])
  const lastPoint = useRef<{ x: number; y: number } | null>(null)

  const cardEls = () => Array.from(gridRef.current?.querySelectorAll<HTMLElement>(':scope > [data-card-id]') ?? [])

  // The others slide to their new places (FLIP): their boxes just before a move…
  const before = useRef<Map<string, DOMRect> | null>(null)
  const snapshot = () => { before.current = new Map(cardEls().map(el => [el.dataset.cardId!, el.getBoundingClientRect()])) }
  // …against their boxes after it.
  useLayoutEffect(() => {
    const prev = before.current
    before.current = null
    if (!prev || reducedMotion()) return
    for (const el of cardEls()) {
      const was = prev.get(el.dataset.cardId!)
      if (!was || el.dataset.cardId === drag?.id) continue
      const now = el.getBoundingClientRect()
      const dx = was.left - now.left
      const dy = was.top - now.top
      if (!dx && !dy) continue
      el.style.transition = 'none'
      el.style.transform = `translate(${dx}px, ${dy}px)`
      requestAnimationFrame(() => {
        el.style.transition = 'transform .28s cubic-bezier(.2, .8, .2, 1)'
        el.style.transform = ''
      })
    }
  }, [ids, drag?.id])

  // Leaving arrange mode mid-drag drops the drag.
  useEffect(() => { if (!arranging) { press.current = null; lastPoint.current = null; setDrag(null) } }, [arranging])

  // The card under the pointer now, in grid terms: the page may have scrolled since the drag began.
  const placeAt = (x: number, y: number) => {
    const p = press.current
    const g = gridRef.current?.getBoundingClientRect()
    if (!p || !g) return
    const list = idsRef.current
    const from = list.indexOf(p.id)
    const to = slotAt(slots.current, x - g.left, y - g.top)
    if (from >= 0 && to >= 0 && to < list.length && to !== from) {
      snapshot()
      onReorder(moveItem(list, from, to))
    }
  }

  // While a card is held: near the scroller's top or bottom edge the page scrolls, and any
  // scroll (that one, a wheel) re-reads the slot under the pointer.
  const dragging = !!drag
  useEffect(() => {
    if (!dragging) return
    const scroller = scrollerOf(gridRef.current)
    const onScroll = () => { const pt = lastPoint.current; if (pt) placeAt(pt.x, pt.y) }
    const target: HTMLElement | Window = scroller ?? window
    target.addEventListener('scroll', onScroll, { passive: true })
    let raf = requestAnimationFrame(function tick() {
      const pt = lastPoint.current
      if (pt) {
        // The top edge is under any sticky bar at the top of the scroller (the window's title and search).
        const bar = scroller?.querySelector<HTMLElement>('.pdc-topbar')
        const top = bar ? bar.getBoundingClientRect().bottom : scroller ? scroller.getBoundingClientRect().top : 0
        const bottom = scroller ? scroller.getBoundingClientRect().bottom : window.innerHeight
        const dy = pt.y < top + EDGE ? -EDGE_SPEED : pt.y > bottom - EDGE ? EDGE_SPEED : 0
        if (dy) { if (scroller) scroller.scrollTop += dy; else window.scrollBy(0, dy) }
      }
      raf = requestAnimationFrame(tick)
    })
    return () => { cancelAnimationFrame(raf); target.removeEventListener('scroll', onScroll) }
    // `placeAt` reads refs only; the loop lives exactly as long as the drag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging])

  // ── Pointer: press, move past a few px to pick up, drop on release ──
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!arranging || (e.pointerType === 'mouse' && e.button !== 0)) return
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-card-id]')
    if (!el || el.parentElement !== gridRef.current) return
    const r = el.getBoundingClientRect()
    const touch = e.pointerType !== 'mouse'
    const p = { id: el.dataset.cardId!, pointerId: e.pointerId, x: e.clientX, y: e.clientY, dx: e.clientX - r.left, dy: e.clientY - r.top, w: r.width, moving: false, touch, held: !touch, timer: 0 }
    press.current = p
    // A finger holds still to pick the card up; until then the page scrolls as usual.
    if (touch) p.timer = window.setTimeout(() => { if (press.current === p) { p.held = true; navigator.vibrate?.(8) } }, TOUCH_HOLD_MS)
    try { gridRef.current?.setPointerCapture(e.pointerId) } catch { /* a pointer that can't be captured still drags while over the grid */ }
  }
  // Once a finger holds a card, its moves drag it — the page must not scroll under it.
  useEffect(() => {
    const grid = gridRef.current
    if (!grid || !arranging) return
    const onTouchMove = (e: TouchEvent) => { if (press.current?.held) e.preventDefault() }
    grid.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => grid.removeEventListener('touchmove', onTouchMove)
  }, [arranging])
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const p = press.current
    if (!p || p.pointerId !== e.pointerId) return
    if (!p.held) {
      // A finger that moves before the hold is a scroll: let it go.
      if (Math.hypot(e.clientX - p.x, e.clientY - p.y) > TOUCH_SLOP) { window.clearTimeout(p.timer); press.current = null }
      return
    }
    if (!p.moving) {
      if (Math.hypot(e.clientX - p.x, e.clientY - p.y) < 4) return
      p.moving = true
      const g = gridRef.current!.getBoundingClientRect()
      slots.current = cardEls().map(el => {
        const b = boxOf(el.getBoundingClientRect())
        return { ...b, left: b.left - g.left, top: b.top - g.top }
      })
    }
    lastPoint.current = { x: e.clientX, y: e.clientY }
    setDrag({ id: p.id, x: e.clientX, y: e.clientY, dx: p.dx, dy: p.dy, w: p.w })
    placeAt(e.clientX, e.clientY)
  }
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const p = press.current
    if (!p || p.pointerId !== e.pointerId) return
    window.clearTimeout(p.timer)
    press.current = null
    lastPoint.current = null
    try { if (gridRef.current?.hasPointerCapture(e.pointerId)) gridRef.current.releasePointerCapture(e.pointerId) } catch { /* already released */ }
    if (p.moving) {
      const list = idsRef.current
      onMoved(p.id, list.indexOf(p.id) + 1, list.length)
    }
    setDrag(null)
  }

  // ── Keyboard: arrows move the focused card; Escape / Enter end arranging ──
  const refocus = useRef<string | null>(null)
  useLayoutEffect(() => {
    const id = refocus.current
    if (!id) return
    refocus.current = null
    const el = cardEls().find(c => c.dataset.cardId === id)
    el?.querySelector<HTMLElement>('.pdc-hit')?.focus({ preventScroll: true })
    // A card moved past the fold comes into view (clear of the window's sticky bar).
    el?.scrollIntoView({ block: 'nearest' })
  })
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!arranging) return
    if (e.key === 'Escape' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); onExit(); return }
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-card-id]')
    if (!el) return
    const list = idsRef.current
    const from = list.indexOf(el.dataset.cardId!)
    const cols = columnsOf(cardEls().map(c => boxOf(c.getBoundingClientRect())))
    const step: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -cols, ArrowDown: cols }
    const to = e.key === 'Home' ? 0 : e.key === 'End' ? list.length - 1 : step[e.key] !== undefined ? from + step[e.key] : null as number | null
    if (to === null) return
    e.preventDefault()
    e.stopPropagation()
    // Past an edge (Up on the first row, Down on the last, Left on the first card…) nothing moves.
    if (to < 0 || to >= list.length || to === from) return
    snapshot()
    refocus.current = list[from]
    onReorder(moveItem(list, from, to))
    onMoved(list[from], to + 1, list.length)
  }

  return (
    <>
      <div
        ref={gridRef}
        className={`pdc-grid${narrow ? ' is-narrow' : ''}${arranging ? ' is-arranging-deck' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
        data-testid="apps-deck"
      >
        {ids.map((id, i) => renderCard(id, { placeholder: drag?.id === id, index: i }))}
        {!arranging && extra}
      </div>
      {drag && createPortal(
        <div className="pdc-drag-layer" style={{ left: drag.x - drag.dx, top: drag.y - drag.dy, width: drag.w }}>
          {renderFloat(drag.id)}
        </div>,
        document.body,
      )}
    </>
  )
}
